// The Milky Way. Sits in the same scene as the solar system at very large
// scale: galaxy radius ~25M units, just inside the existing 40M skybox.
// Cross-fades with the inside-the-galaxy panorama by the camera's distance
// from the Sun.
//
// Layers, back to front:
//   - deep field starbox (follows the camera, far galaxies + stars)
//   - globular clusters
//   - the galaxy itself (src/galaxyParticles.js): stars, clouds, HII knots
//     and dust as 3D particles, drawn back to front into their own target
import * as THREE from 'three';
import { createParticleGalaxy, MW_STRUCTURE, GALAXY_LOOK } from './galaxyParticles.js';
import { galacticToScene } from './sky/frame.js';

// ============================================================
// Real Milky Way physical parameters (Vallée 2008 + Hou & Han
// 2014 + Bland-Hawthorn & Gerhard 2016). The visualization
// derives every length, density, and velocity from these so
// proportions, profile shapes, and rotation curve match the
// real galaxy. The only freedom we take is the visual scale
// factor that maps kiloparsecs into scene-units.
// ============================================================
const MW = {
  // Geometry, in kiloparsecs (kpc).
  diskRadiusKpc: 16.0,         // visible disk extent
  diskScaleLengthKpc: 2.6,     // exponential disk scale length R_d
  diskScaleHeightKpc: 0.30,    // thin disk scale height h_z
  thickDiskScaleHeightKpc: 1.0, // thick-disk h_z (Gilmore & Reid 1983)
  thickDiskFraction: 0.18,     // fraction of disc mass in thick disc
  bulgeRadiusKpc: 2.0,         // bulge truncation
  bulgeScaleKpc: 0.55,         // Hernquist scale length 'a'
  // Bulge vertical scale. Real Milky Way bulge is mildly oblate but
  // not as flat as 0.5:1 — pushing this closer to the bulge radius
  // makes the bulge read as a rounder spheroid rather than a
  // tubular puck when viewed from oblique angles.
  bulgeScaleHeightKpc: 1.55,
  nucleusRadiusKpc: 0.4,       // nuclear star cluster footprint
  barLengthKpc: 5.0,           // bar half-length (full ~5 kpc each side)
  barWidthKpc: 1.0,            // bar half-width
  sunRadiusKpc: 8.0,           // Sun's distance from galactic center

  // Spiral arm geometry. Vallée 2008 4-arm symmetric model. We use
  // 13.5° pitch — close to the canonical Milky Way value (12.5°)
  // and well within the literature range (9.9° Eilers+ 2020 to 17°
  // Reid+ 2019). Tighter pitch gives arms visible curvature at all
  // radii including the outer disc where loose pitches project as
  // straight ribbons at oblique angles. Per-arm pitch variation
  // (±9% in armPitchMul) keeps the arms from looking identical so
  // tight pitch doesn't read as a phonograph-ring pattern face-on.
  armCount: 4,
  pitchAngleDeg: 13.5,
  // Relative on-arm star density per arm. Two arms (Perseus,
  // Scutum-Centaurus) carry the bulk of OB associations; the
  // other two (Sagittarius, Norma) are minor arms. Sums to 1.
  armWeights: [0.34, 0.18, 0.32, 0.16],

  // Kinematics. The Milky Way rotation curve is approximately
  // flat at v_flat = 220 km/s outside ~3 kpc.
  vFlatKmS: 220.0,
  vRiseKpc: 3.0,               // rotation curve flattens beyond this
  // Pattern speeds (Bland-Hawthorn & Gerhard 2016).
  patternSpeedKmSKpc: 25.0,    // spiral arm pattern speed (Omega_p)
  barPatternSpeedKmSKpc: 39.0, // bar pattern speed
};

// Visual scale: how many scene-units per kpc. Chosen so the disk
// half-diameter lands around 25M units, just inside the existing
// 40M skybox. Everything else (disk, bulge, bar, sun radius) is
// derived from this single conversion factor.
const SCENE_UNITS_PER_KPC = 25_000_000 / MW.diskRadiusKpc; // ≈ 1.5625M

// Time compression: real Milky Way orbital period at the sun is
// ~225 Myr; for a useful visualization we want a galaxy "year" to
// take ~5 minutes at timeSpeed = 1. Velocities entered as km/s
// get multiplied by this factor to convert into scene-units per
// real second of accumulated galaxy time.
const VISUAL_VELOCITY_SCALE = (() => {
  // omega(R_sun) wanted: 2π / (5 min * 60 s/min) at timeSpeed=1
  // omega(R_sun) = vFlat / R_sun, so vFlat = omega * R_sun
  const desiredOmegaAtSun = (2 * Math.PI) / (5 * 60); // rad / sec
  const sunRadiusUnits = MW.sunRadiusKpc * SCENE_UNITS_PER_KPC;
  const sceneVFlatNeeded = desiredOmegaAtSun * sunRadiusUnits;
  return sceneVFlatNeeded / MW.vFlatKmS; // scene-units per (km/s)
})();

// Derived scene-unit constants. These carry the real proportions
// into the procedural sampling.
const SCENE = {
  diskRadius: MW.diskRadiusKpc * SCENE_UNITS_PER_KPC,
  diskScaleLength: MW.diskScaleLengthKpc * SCENE_UNITS_PER_KPC,
  diskScaleHeight: MW.diskScaleHeightKpc * SCENE_UNITS_PER_KPC,
  thickDiskScaleHeight: MW.thickDiskScaleHeightKpc * SCENE_UNITS_PER_KPC,
  bulgeRadius: MW.bulgeRadiusKpc * SCENE_UNITS_PER_KPC,
  bulgeScale: MW.bulgeScaleKpc * SCENE_UNITS_PER_KPC,
  bulgeScaleHeight: MW.bulgeScaleHeightKpc * SCENE_UNITS_PER_KPC,
  nucleusRadius: MW.nucleusRadiusKpc * SCENE_UNITS_PER_KPC,
  barHalfLength: MW.barLengthKpc * SCENE_UNITS_PER_KPC,
  barHalfWidth: MW.barWidthKpc * SCENE_UNITS_PER_KPC,
  sunRadius: MW.sunRadiusKpc * SCENE_UNITS_PER_KPC,
  vRise: MW.vRiseKpc * SCENE_UNITS_PER_KPC,
  vFlat: MW.vFlatKmS * VISUAL_VELOCITY_SCALE,
  // Negative sign so the disc rotates in the same direction as the
  // real Milky Way as viewed from galactic north (clockwise from our
  // perspective looking down at the disc), matching standard
  // astronomical convention.
  patternSpeed: -MW.patternSpeedKmSKpc * VISUAL_VELOCITY_SCALE
    / SCENE_UNITS_PER_KPC,
  barPatternSpeed: -MW.barPatternSpeedKmSKpc * VISUAL_VELOCITY_SCALE
    / SCENE_UNITS_PER_KPC,
  // Disc inner radius. The Hernquist bulge fades into its tail by
  // ~2–3 scale lengths, so by the time we reach the bulge truncation
  // the bulge is already mostly transparent. Putting the disc inner
  // edge well inside the bulge scale length means the spiral arms
  // physically thread into the bulge region — bulge stars dominate
  // there photometrically, but disc/arm stars are still present, so
  // there is no dark ring between the core and the arms.
  diskInnerRadius: MW.bulgeScaleKpc * 1.2 * SCENE_UNITS_PER_KPC,
};

const DEFAULTS = {
  // Deep-field starbox. Density bumped substantially so galactic-
  // scale views (BH, exterior galaxy) read as Hubble-deep-field rich
  // rather than sparse — the bigger backdrop reinforces the
  // illusion that the BH and galaxy are sitting inside a populated
  // universe rather than floating in empty void.
  deepFieldDesktop: 28000,
  deepFieldMobile: 7000,
  deepFieldRadius: 30_000_000,
  deepFieldShowpieces: 60,    // bigger tinted "distant galaxies"

  // Pre-spin offset, in galaxy seconds: the pattern angle the first frame
  // starts from.
  prespinSeconds: 600,

  // Globular cluster halo — tiny fuzzy clusters in a roughly r^-2
  // spherical distribution out to ~1.5 disc radii. Visible texture,
  // not features: each one is a single dim soft sprite.
  globularDesktop: 56,
  globularMobile: 30,
};

// Color palette. Cool/warm contrast between arms (cool blue OB
// stars) and bulge (warm yellow Pop II) is what gives Hurt-style
// galaxy paintings their depth. The saturated arm blue here is
// pushed past the conservative starting palette for stronger
// spectral contrast.
const COLORS = {
  bulgeCore: new THREE.Color(0xffe6c0),
  bulgeShoulder: new THREE.Color(0xffc486),
  barWarm: new THREE.Color(0xffce8e),
  armBlue: new THREE.Color(0xa6c0ff),     // deeper cool blue
  armWhite: new THREE.Color(0xdce6ff),    // slightly cooled white
  hii: new THREE.Color(0xff6f9d),
  interArm: new THREE.Color(0xffe5c2),
  haloRed: new THREE.Color(0xd99570),
  // Extinction dust: very dark brown-black. Drawn with normal
  // alpha blending so it darkens the glow behind it instead of
  // adding light.
  dust: new THREE.Color(0x140e08),
  // Globular clusters: warm white, the integrated light of ancient
  // metal-poor populations.
  globular: new THREE.Color(0xfff0d8),
};

function smoothstep(edge0, edge1, x) {
  if (edge0 === edge1) return x < edge0 ? 0 : 1;
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function createDeepField(opts, renderer, isMobile) {
  const N = isMobile ? opts.deepFieldMobile : opts.deepFieldDesktop;
  const radius = opts.deepFieldRadius;
  const showpieceCount = isMobile
    ? Math.floor(opts.deepFieldShowpieces * 0.5)
    : opts.deepFieldShowpieces;

  const positions = new Float32Array(N * 3);
  const colors = new Float32Array(N * 3);
  const sizes = new Float32Array(N);

  const tmpColor = new THREE.Color();

  for (let i = 0; i < N; i++) {
    // Uniform on sphere. Tiny variation in radius so we don't draw a
    // mathematically perfect shell. We also bias points toward
    // clumps — real deep-field surveys show large-scale structure
    // where galaxies cluster on the cosmic web rather than being
    // uniformly distributed. Three sinusoidal modulations layered
    // over the uniform sample give a believable cluster pattern.
    const u = Math.random() * 2 - 1;
    const phi = Math.acos(u);
    const theta = Math.random() * Math.PI * 2;
    const r = radius * (0.94 + Math.random() * 0.12);
    positions[i * 3]     = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
    positions[i * 3 + 2] = r * Math.cos(phi);

    // First showpieceCount points are oversized "distant galaxies"
    // with strong color tints — the eye latches onto these as
    // resolved background galaxies rather than just stars.
    const isShowpiece = i < showpieceCount;

    if (isShowpiece) {
      const hueRoll = Math.random();
      if (hueRoll < 0.30)      tmpColor.setHSL(0.06, 0.55, 0.55);  // strong red
      else if (hueRoll < 0.55) tmpColor.setHSL(0.60, 0.45, 0.58);  // strong blue
      else if (hueRoll < 0.78) tmpColor.setHSL(0.13, 0.32, 0.65);  // golden
      else                     tmpColor.setHSL(0.85, 0.30, 0.55);  // dusty pink
      const lum = 0.55 + Math.random() * 0.50;
      colors[i * 3]     = tmpColor.r * lum;
      colors[i * 3 + 1] = tmpColor.g * lum;
      colors[i * 3 + 2] = tmpColor.b * lum;
      sizes[i] = 3.5 + Math.random() * 3.0;
    } else if (Math.random() < 0.12) {
      // 12% small distant galaxies — slight color tint, modest size
      const hueRoll = Math.random();
      if (hueRoll < 0.4)      tmpColor.setHSL(0.05, 0.40, 0.55);
      else if (hueRoll < 0.7) tmpColor.setHSL(0.60, 0.32, 0.55);
      else                    tmpColor.setHSL(0.13, 0.18, 0.62);
      const lum = 0.40 + Math.random() * 0.45;
      colors[i * 3]     = tmpColor.r * lum;
      colors[i * 3 + 1] = tmpColor.g * lum;
      colors[i * 3 + 2] = tmpColor.b * lum;
      sizes[i] = 1.4 + Math.random() * 1.6;
    } else {
      // Background field stars — tiny, near-white.
      const lum = 0.25 + Math.random() * 0.55;
      const tintG = 0.94 + Math.random() * 0.10;
      const tintB = 0.92 + Math.random() * 0.12;
      colors[i * 3]     = lum;
      colors[i * 3 + 1] = lum * tintG;
      colors[i * 3 + 2] = lum * tintB;
      sizes[i] = 0.6 + Math.random() * 0.7;
    }
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geom.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geom.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
  geom.boundingSphere = new THREE.Sphere(new THREE.Vector3(), radius * 1.1);

  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uOpacity: { value: 0.0 },
      uPixelRatio: { value: renderer.getPixelRatio() },
    },
    vertexShader: /* glsl */`
      attribute float size;
      varying vec3 vColor;
      uniform float uPixelRatio;
      void main() {
        vColor = color;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        // Fixed pixel size — by design the deep field has no parallax;
        // it's a backdrop, not a foreground.
        gl_PointSize = size * uPixelRatio * 1.6;
      }
    `,
    fragmentShader: /* glsl */`
      varying vec3 vColor;
      uniform float uOpacity;
      void main() {
        vec2 p = gl_PointCoord - vec2(0.5);
        float d = length(p);
        float a = exp(-d * d * 36.0) * step(d, 0.5);
        gl_FragColor = vec4(vColor, a * uOpacity);
      }
    `,
    vertexColors: true,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    depthTest: false,
  });

  const points = new THREE.Points(geom, mat);
  points.name = 'galaxyDeepField';
  points.frustumCulled = false; // follows camera, never out of view
  return { points, material: mat, geometry: geom };
}

// Globular cluster halo. A few dozen ancient clusters orbit the
// Milky Way in a roughly spherical halo out past the disc edge,
// concentrated toward the center. Each is a single tiny fuzzy
// sprite — warm white, very dim, more texture than feature. Real
// globular orbits are essentially random with no net rotation, so
// the layer has no time uniform and stays static while the disc
// spins, which also keeps it trivially seamless across the wrapped
// galaxy clock.
function createGlobularClusters(opts, renderer, isMobile) {
  const N = isMobile ? opts.globularMobile : opts.globularDesktop;

  const positions = new Float32Array(N * 3);
  const colors = new Float32Array(N * 3);
  const sizes = new Float32Array(N);

  const tmpColor = new THREE.Color();

  for (let i = 0; i < N; i++) {
    // Uniform sampling in radius gives an r^-2 density falloff —
    // strongly centrally concentrated, sparse at the rim, like the
    // real cluster system.
    const r = SCENE.bulgeRadius * 0.8
      + Math.random() * (SCENE.diskRadius * 1.5 - SCENE.bulgeRadius * 0.8);
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    positions[i * 3]     = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
    positions[i * 3 + 2] = r * Math.cos(phi);

    // Warm white drifting slightly amber, very dim individually.
    tmpColor.copy(COLORS.globular).lerp(COLORS.bulgeShoulder, Math.random() * 0.30);
    const intensity = 0.10 + Math.random() * 0.14;
    colors[i * 3]     = tmpColor.r * intensity;
    colors[i * 3 + 1] = tmpColor.g * intensity;
    colors[i * 3 + 2] = tmpColor.b * intensity;
    sizes[i] = 2.6 + Math.random() * 3.4;
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geom.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geom.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
  geom.boundingSphere = new THREE.Sphere(new THREE.Vector3(), SCENE.diskRadius * 1.6);

  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uPixelRatio: { value: renderer.getPixelRatio() },
      uOpacity: { value: 0.0 },
      uReferenceDist: { value: SCENE.diskRadius * 1.4 },
    },
    vertexShader: /* glsl */`
      attribute float size;
      varying vec3 vColor;
      uniform float uPixelRatio;
      uniform float uReferenceDist;
      void main() {
        vColor = color;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        float depth = max(1.0, -mvPosition.z);
        float px = size * (uReferenceDist / depth);
        gl_PointSize = clamp(px * uPixelRatio, 1.5, 22.0);
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: /* glsl */`
      varying vec3 vColor;
      uniform float uOpacity;
      void main() {
        vec2 p = gl_PointCoord - vec2(0.5);
        float d = length(p);
        // Compact core wrapped in a wider fuzz skirt — reads as an
        // unresolved cluster, not a single star.
        float core = exp(-d * d * 42.0);
        float fuzz = exp(-d * d * 9.0) * 0.35;
        float a = clamp(core + fuzz, 0.0, 1.0) * step(d, 0.5);
        gl_FragColor = vec4(vColor, a * uOpacity);
      }
    `,
    vertexColors: true,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    depthTest: true,
  });

  const points = new THREE.Points(geom, mat);
  points.name = 'milkyWayGlobulars';
  return { points, material: mat, geometry: geom };
}

// Where the Milky Way is first shown from (focusOnObject in main.js, for the
// galaxy and its vista): degrees above the disc's plane (90 face-on, 0 edge-on),
// degrees round its axis from the centre's line to the Sun (180 the far side),
// and the distance from the centre in disc radii. The tuner dock's Galaxy tab
// sets it, and its "Use this view" takes it from the camera.
export const GALAXY_VIEW = { elevation: 10, azimuth: 102, distance: 3.2 };

export function createGalaxy(scene, renderer, options = {}) {
  const opts = { ...DEFAULTS, ...options };

  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  const isMobile = /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);

  // ---- The galaxy: 3D particles (src/galaxyParticles.js) ----
  const BAR_ANGLE = THREE.MathUtils.degToRad(MW_STRUCTURE.barAngleDeg);
  const particles = createParticleGalaxy({ renderer, unitsPerKpc: SCENE_UNITS_PER_KPC, isMobile, barAngle: BAR_ANGLE });

  // The galaxy object: tilted, placed, and carrying the particles, which
  // turn inside it with the spiral pattern.
  const root = new THREE.Group();
  root.name = 'milkyWay';
  root.userData = {
    name: 'Milky Way',
    type: 'Spiral galaxy',
    description: 'Our home galaxy. SBbc barred spiral, ~100,000 ly across, ~400 billion stars.',
    isGalaxy: true,
    radius: SCENE.diskRadius * 0.4,
    galaxyRadius: SCENE.diskRadius,
  };
  // Orientation: the real one. The disc's normal is the north galactic pole
  // and its centre lies toward l = 0, b = 0 (Sagittarius), both carried into
  // the scene by src/sky/frame.js, so the band of the sky map (drawn in the
  // same frame) and the 3D disc are one band at the hand-over.
  const nrm = galacticToScene(0, 90);
  const toCentre = galacticToScene(0, 0);
  // Local frame: z the disc normal (galactic north, the Sun's side), x from
  // the centre toward the Sun.
  const ax = toCentre.clone().negate();
  const ay = new THREE.Vector3().crossVectors(nrm, ax);
  root.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(ax, ay, nrm));
  // Until October 2026 the disc lay in the old panorama's band, measured as
  // it hung on the scene then: normal (0.099, 0.992, 0.076), centre toward
  // (0.999, 0.043, -0.006), which put the band along the planets' orbits.
  // The screensaver's galaxy framings (src/screensaver/tour.js) were written
  // against that disc in world axes; framingQuaternion carries them onto
  // this one, so each shot still sees the disc as it was composed.
  const OLD_NORMAL = new THREE.Vector3(0.099, 0.992, 0.076).normalize();
  const OLD_CENTRE = new THREE.Vector3(0.999, 0.043, -0.006);
  const oldCentre = OLD_CENTRE.clone().addScaledVector(OLD_NORMAL, -OLD_CENTRE.dot(OLD_NORMAL)).normalize();
  const oldBasis = new THREE.Matrix4().makeBasis(oldCentre.clone().negate(), new THREE.Vector3().crossVectors(OLD_NORMAL, oldCentre.clone().negate()), OLD_NORMAL);
  const framingQuaternion = root.quaternion.clone()
    .multiply(new THREE.Quaternion().setFromRotationMatrix(oldBasis).invert());
  // And a point placed against the old disc (the stellar black hole, main.js)
  // carried to the same place against this one.
  const oldCentreOffset = oldCentre.clone().multiplyScalar(MW_STRUCTURE.sunR * SCENE_UNITS_PER_KPC)
    .addScaledVector(OLD_NORMAL, -MW_STRUCTURE.sunZ * SCENE_UNITS_PER_KPC);

  // The Sun (the scene origin) sits where it is: R0 = 8.2 kpc from the
  // centre and 25 pc above the plane (BHG16 3.2.4, 3.3).
  const centerOffset = toCentre.clone().multiplyScalar(MW_STRUCTURE.sunR * SCENE_UNITS_PER_KPC)
    .addScaledVector(nrm, -MW_STRUCTURE.sunZ * SCENE_UNITS_PER_KPC);
  root.position.copy(centerOffset);
  root.add(particles.composite);
  scene.add(root);

  // ---- Globular cluster halo ----
  const globulars = createGlobularClusters(opts, renderer, isMobile);
  globulars.points.quaternion.copy(root.quaternion);
  globulars.points.position.copy(centerOffset);
  globulars.points.renderOrder = -912;
  globulars.points.frustumCulled = true;
  scene.add(globulars.points);

  // ---- Deep field starbox ----
  const deep = createDeepField(opts, renderer, isMobile);
  deep.points.renderOrder = -990;
  scene.add(deep.points);

  const pm = particles.material.uniforms;
  let visible = true;

  return {
    object: root,
    framingQuaternion,
    fromOldFrame: (v, out = new THREE.Vector3()) => out.copy(v).sub(oldCentreOffset).applyQuaternion(framingQuaternion).add(centerOffset),
    particles,
    particleMaterial: particles.material,
    globularPoints: globulars.points,
    globularMaterial: globulars.material,
    deepField: deep.points,
    deepFieldMaterial: deep.material,
    options: opts,

    setOpacity(value) {
      const v = Math.max(0, Math.min(1, value));
      const on = v > 0.001;
      pm.uOpacity.value = v;
      globulars.material.uniforms.uOpacity.value = v;
      deep.material.uniforms.uOpacity.value = v;
      if (on !== visible) {
        visible = on;
        particles.composite.visible = on;
        globulars.points.visible = on;
        deep.points.visible = on;
      }
    },

    // Generates the particles again after a change to the look (the tuner
    // dock's Galaxy tab); returns the generation time in ms.
    rebuild() {
      return particles.rebuild();
    },

    setPixelRatio(pr) {
      globulars.material.uniforms.uPixelRatio.value = pr;
      deep.material.uniforms.uPixelRatio.value = pr;
    },

    /**
     * One-shot per-frame update: turns the pattern, follows the camera with
     * the deep field, and cross-fades the galaxy and the panorama by the
     * camera's distance from the Sun.
     */
    tick(camera, galaxyTime, distFromSun, fadeRange = {}) {
      // The disc turns rigidly with the spiral pattern.
      const tNow = opts.prespinSeconds + galaxyTime;
      particles.group.rotation.z = SCENE.patternSpeed * tNow;
      particles.group.updateMatrix();
      const cam = particles.cameraLocal(camera, root);
      // Exposure. From outside, the disc is exposed as a galaxy photograph
      // is; from inside, the camera looks along the plane through many
      // times the starlight of a face-on sightline, so it stops down.
      const rCam = Math.hypot(cam.x, cam.y);
      const inside = (1 - smoothstep(0.6, 4.0, Math.abs(cam.z))) * (1 - smoothstep(13, 22, rCam));
      // A thin disc's surface brightness rises with the path through it, as
      // 1 / cos(inclination): seen edge-on it is several times brighter than
      // face-on, and a photograph of it is exposed shorter. The galaxy stops
      // down toward edge-on so the band and its lane do not clip.
      const view = Math.abs(cam.z) / Math.max(Math.hypot(cam.x, cam.y, cam.z), 1e-6);
      const L = GALAXY_LOOK;
      const incl = L.edgeOnExposure + (1 - L.edgeOnExposure) * smoothstep(0.08, 0.7, view);
      pm.uExposure.value = L.exposure * (1 - L.insideDim * inside) * incl;
      particles.syncLook();

      // Deep field follows the camera so it always sits behind
      // everything else, regardless of how far the user has zoomed.
      deep.points.position.copy(camera.position);

      // Cross-fade based on camera distance. window.__galaxyFade
      // ({ fadeStart, fadeEnd }) overrides the range for checks.
      const ov = (typeof window !== 'undefined' && window.__galaxyFade) || null;
      const fadeStart = (ov ? ov.fadeStart : fadeRange.fadeStart) ?? 200_000;
      const fadeEnd = (ov ? ov.fadeEnd : fadeRange.fadeEnd) ?? 5_000_000;
      const t = smoothstep(fadeStart, fadeEnd, distFromSun);
      this.setOpacity(t);
      return { galaxyOpacity: t, panoramaOpacity: 1 - t };
    },

    dispose() {
      scene.remove(root);
      scene.remove(globulars.points);
      scene.remove(deep.points);
      particles.dispose();
      globulars.geometry.dispose();
      globulars.material.dispose();
      deep.geometry.dispose();
      deep.material.dispose();
    },
  };
}
